import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useOutletContext } from 'react-router-dom';
import { getQuery } from '@/helpers/utils';
import { useModal } from '@/providers/modal';
import Pathes from '@/helpers/pathes';
import { APP_LINKS } from '@/helpers/links';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { PeriodSelect } from '@/components/TaxReport/PeriodSelect';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { DateRangePicker } from '@/kit/DatePickers/DateRangePicker';
import { MonthRangePicker } from '@/kit/DatePickers/MonthRangePicker';
import { OperationTypeSelect } from '@/components/TaxReport/OperationTypeSelect';
import { FileFormatPicker } from '@/components/TaxReport/FileFormatPicker';
import { useDownloadLink } from '@/hooks/useDownloadLink';
import r from '@/kit/RoundButton/button.module.scss';
import s from './index.module.scss';

const id = MODAL_IDS.report_download_modal;

const TYPES = {
  cash_movements: {
    path: Pathes.Activity.cashMovements,
    fileName: 'Cash-movements',
  },
  portfolio_movements: {
    path: Pathes.Activity.portfolioMovements,
    fileName: 'Portfolio-movements',
  },
  tax_report: {
    path: Pathes.Activity.taxReport,
    fileName: 'Tax-reports',
  },
};

export const ReportDownloadModal = props => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

const Modal = ({ type, params }) => {
  const { onDownload } = useDownloadLink();
  const { closeModal } = useModal();
  const location = useLocation();
  const { type: accountType } = useOutletContext();
  const { t } = useTranslation('modal');

  const modal = useMemo(() => {
    switch (location.pathname) {
      case APP_LINKS.cashMovements:
        return {
          path: APP_LINKS.cashMovements,
          title: t('report_download_modal.export_cash_movements_report'),
        };
      case APP_LINKS.portfolioMovements:
        return {
          path: APP_LINKS.portfolioMovements,
          title: t('report_download_modal.export_portfolio_movements_report'),
        };
      case APP_LINKS.taxReport:
        return {
          path: APP_LINKS.taxReport,
          title: t('report_download_modal.export_tax_report'),
        };
      default:
        return {};
    }
  }, [location.pathname]);

  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ ...params, format: 'pdf' });

  const onFilterChange = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));

  const onDownloadReport = async () => {
    try {
      setLoading(true);
      await onDownload({
        url:
          TYPES[type].path +
          getQuery({
            date_from: filters.range[0],
            date_to: filters.range[1],
            operation_types: filters.operation_types,
            grouping: JSON.stringify(filters.grouping),
            format: filters.format,
            type: accountType,
          }),
        filename: [TYPES[type].fileName, ...filters.range].filter(i => i).join('-'),
      });
    } finally {
      setLoading(false);
    }
  };

  const content = (
    <div className={s.modal}>
      <div>
        <h1 className="f-hero-h5">{modal.title}</h1>
        <p className="f-small-regular">{t('report_download_modal.data_retrieved')}</p>
      </div>
      <div className={s.content}>
        <PeriodSelect range={filters.range} onChange={range => onFilterChange('range', range)} />
        {modal.path === APP_LINKS.portfolio_movements ? (
          <MonthRangePicker
            range={filters.range}
            setRange={range => onFilterChange('range', range)}
            hideCalendar
            maxDate={new Date()}
          />
        ) : (
          <DateRangePicker
            range={filters.range}
            setRange={range => onFilterChange('range', range)}
            hideCalendar
            maxDate={new Date()}
          />
        )}
        {modal.path === APP_LINKS.cashMovements && (
          <OperationTypeSelect
            menuPlacement="top"
            selected={filters.operation_types}
            onChange={operation_types => onFilterChange('operation_types', operation_types)}
          />
        )}
        <FileFormatPicker
          value={filters.format}
          onChange={format => onFilterChange('format', format)}
        />
      </div>
      <div className={s.footer}>
        <button type="button" className={r.grey_base} onClick={() => closeModal(id)}>
          {t('report_download_modal.cancel')}
        </button>
        <button
          type="button"
          disabled={loading}
          className={r.black_large}
          onClick={onDownloadReport}
        >
          {loading ? (
            t('report_download_modal.generating_report')
          ) : (
            <>
              {t('report_download_modal.export')} <span>{filters.format}</span>
            </>
          )}
        </button>
      </div>
    </div>
  );

  return (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};
